import React, { useState, useEffect } from 'react';
import {
  Drawer,
  Box,
  Button,
  TextField,
  Grid,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Chip,
  Typography,
  IconButton,
  CircularProgress,
  Autocomplete,
  Alert
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { useLead } from '../../contexts/LeadContext';
import AgentSelect from '../../components/common/AgentSelect';
import { LEAD_THEME } from '../../utils/lead_helper_v2';

const DRAWER_WIDTH = 500;

const mkFieldStyles = (accentColor = LEAD_THEME.green) => ({
  '& .MuiInputBase-root': { height: 40, fontSize: 12, borderRadius: 1.5 },
  '& .MuiInputLabel-root': { fontSize: 12, color: LEAD_THEME.subtle, '&.Mui-focused': { color: accentColor } },
  '& .MuiOutlinedInput-root': {
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: accentColor, borderWidth: 1.5 },
  },
  '& .MuiInputBase-input::placeholder': { fontSize: 12, opacity: 0.6 },
});

const SectionHeader = ({ title, subtitle, complete }) => (
  <Box sx={{ mb: 1.25, display: 'flex', alignItems: 'flex-start', gap: 1 }}>
    <Box sx={{
      width: 26, height: 26, borderRadius: 1.5, flexShrink: 0, mt: 0.1,
      bgcolor: complete ? LEAD_THEME.greenSf : LEAD_THEME.greenLt,
      color: complete ? LEAD_THEME.green : LEAD_THEME.ink2,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      border: `1.5px solid ${complete ? LEAD_THEME.green : LEAD_THEME.border}`,
      transition: 'all 0.2s ease',
    }}>
      {complete ? <CheckCircleOutlineIcon sx={{ fontSize: 14 }} /> : <PersonOutlineIcon sx={{ fontSize: 14 }} />}
    </Box>
    <Box>
      <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: LEAD_THEME.ink, lineHeight: 1.3 }}>
        {title}
      </Typography>
      {subtitle && (
        <Typography sx={{ fontSize: 10.5, color: LEAD_THEME.subtle, display: 'block', lineHeight: 1.4 }}>
          {subtitle}
        </Typography>
      )}
    </Box>
  </Box>
);

const Divider = () => <Box sx={{ height: 1, bgcolor: LEAD_THEME.borderL }} />;

const COUNTRY_CODES = [
  { value: '+91', label: '+91 (India)' },
  { value: '+1', label: '+1 (USA)' },
  { value: '+44', label: '+44 (UK)' },
  { value: '+61', label: '+61 (Australia)' },
  { value: '+65', label: '+65 (Singapore)' },
];

const CreateNewLead = ({ open, onClose, defaultFilters, agents=[], onLeadCreated }) => {
  const { addNewLead, isLoading } = useLead();
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    country_code: '+91',
    mobile: '',
    source: 'manual',
    assigned_to: '',
    tags: [],
    notes: '',
    channel_partner: '',
  });
  
  const [errors, setErrors] = useState({});

  // Load default filter options when component mounts
  const [sourceOptions, setSourceOptions] = useState([]);
  const [tagOptions, setTagOptions] = useState([]);
  const [channelPartnerOptions, setChannelPartnerOptions] = useState([]);
  const [agentsList, setAgentsList] = useState(agents || []);
  
  useEffect(() => {
    if (agents && agents.length > 0) {
      setAgentsList(agents);
    }
  }, [agents]);
  
  useEffect(() => {
    // If defaultFilters is available, extract options
    if (defaultFilters && Object.keys(defaultFilters).length > 0) {
      // Source is now an array of objects with value and label
      if (defaultFilters.source && Array.isArray(defaultFilters.source)) {
        setSourceOptions(defaultFilters.source);
      }
      
      // Tags array remains as is
      if (defaultFilters.tags && Array.isArray(defaultFilters.tags)) {
        setTagOptions(defaultFilters.tags);
      }

      // Channel partner is now an array of objects with value and label
      if (defaultFilters.channel_partner && Array.isArray(defaultFilters.channel_partner)) {
        setChannelPartnerOptions(defaultFilters.channel_partner);
      }
    }
  }, [defaultFilters]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Special handling for mobile field
    if (name === 'mobile') {
      // Remove all non-digit characters
      const digitsOnly = value.replace(/\D/g, '');
      
      // Limit to 10 digits
      const limitedValue = digitsOnly.slice(0, 10);
      
      setFormData({
        ...formData,
        [name]: limitedValue,
      });
    } else {
      setFormData({
        ...formData,
        [name]: value,
      });
    }
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: '',
      });
    }
  };

  // Add this helper function to validate source
  const isValidSource = (value) => {
    // Always allow 'manual' source
    if (value === 'manual') return true;

    // Check if it's a valid selection from options
    const isExistingSource = sourceOptions.some(option => 
      option.value === value || option.label === value
    );

    // If it's not from options, validate the new text entry
    if (!isExistingSource) {
      // Must be at least 3 characters and contain only letters, numbers, spaces, and basic punctuation
      return value.trim().length >= 3 && /^[a-zA-Z0-9\s.,&-]+$/.test(value);
    }

    return true;
  };

  const handleAutocompleteChange = (name, value) => {
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Source-specific validation - only validate if there's a value, allow empty while typing
    if (name === 'source') {
      // Clear error if field is being cleared (allow empty while typing)
      if (!value || (typeof value === 'string' && !value.trim())) {
        setErrors(prev => ({
          ...prev,
          source: ''
        }));
      } else if (!isValidSource(value)) {
        // Only show validation error if value is invalid and not empty
        setErrors(prev => ({
          ...prev,
          source: 'Enter a valid source (min 3 characters, only letters, numbers, spaces, and basic punctuation)'
        }));
      } else {
        setErrors(prev => ({
          ...prev,
          source: ''
        }));
      }
    } else if (errors[name]) {
      // Clear other field errors
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleTagsChange = (event, newValue) => {
    // Ensure tags are stored as strings for consistency
    const processedTags = newValue.map(tag => {
      if (typeof tag === 'string') {
        return tag;
      }
      return tag.label || tag.value || tag;
    });
    
    setFormData(prev => ({
      ...prev,
      tags: processedTags
    }));
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }
    
    if (!formData.email && !formData.mobile) {
      newErrors.email = 'Either email or mobile is required';
      newErrors.mobile = 'Either email or mobile is required';
    }
    
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email format';
    }
    
    if (formData.mobile) {
      if (!/^\d{10}$/.test(formData.mobile)) {
        newErrors.mobile = 'Mobile number should be 10 digits';
      }
    }

    // Source validation
    if (!formData.source || !formData.source.trim()) {
      newErrors.source = 'Source is required';
    } else if (!isValidSource(formData.source)) {
      newErrors.source = 'Enter a valid source (min 3 characters, only letters, numbers, spaces, and basic punctuation)';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    
    try {
      const leadData = { ...formData };
      
      // Convert assigned_to to number if it exists
      if (leadData.assigned_to && leadData.assigned_to !== '') {
        leadData.assigned_to = parseInt(leadData.assigned_to, 10);
      }
      
      // Remove empty fields to keep the request payload clean
      Object.keys(leadData).forEach(key => {
        if (leadData[key] === '' || 
            (Array.isArray(leadData[key]) && leadData[key].length === 0)) {
          delete leadData[key];
        }
      });

      await addNewLead(leadData);
      handleClose();
      // Trigger refresh in parent component (LeadsV2) to use v2 endpoint
      if (onLeadCreated) {
        onLeadCreated();
      }
    } catch (error) {
      console.error('Error creating lead:', error);
      
      // Handle API validation errors
      if (error.response && error.response.data) {
        const apiErrors = {};
        
        // Handle single lead error response
        if (typeof error.response.data === 'object') {
          // Handle direct field errors
          Object.entries(error.response.data).forEach(([key, value]) => {
            apiErrors[key] = Array.isArray(value) ? value.join(', ') : value;
          });
        }
        
        // Handle common field mappings
        if (apiErrors.contact) {
          // If contact has errors, map them to the appropriate fields
          if (apiErrors.contact.mobile) {
            apiErrors.mobile = apiErrors.contact.mobile;
          }
          if (apiErrors.contact.email) {
            apiErrors.email = apiErrors.contact.email;
          }
          if (apiErrors.contact.name) {
            apiErrors.name = apiErrors.contact.name;
          }
        }
        
        // Handle non-field specific errors
        if (error.response.data.detail) {
          apiErrors.general = error.response.data.detail;
        } else if (error.response.data.error) {
          // Handle simplified error messages from backend
          apiErrors.general = error.response.data.error;
        }
        
        setErrors(apiErrors);
      } else if (error.message) {
        // Handle network or other errors
        setErrors({ general: error.message });
      }
    }
  };

  const handleClose = () => {
    setFormData({
      name: '',
      email: '',
      country_code: '+91',
      mobile: '',
      source: 'manual',  // Reset to manual
      assigned_to: '',
      tags: [],
      notes: '',
      channel_partner: '',
    });
    setErrors({});
    onClose();
  };
  
  const handleAgentChange = (agentId, selectedAgent) => {
    setFormData(prev => ({
      ...prev,
      assigned_to: agentId || selectedAgent?.id || selectedAgent?.agent_id || ''
    }));
    
    // Clear error when agent is selected
    if (errors.assigned_to) {
      setErrors(prev => ({
        ...prev,
        assigned_to: ''
      }));
    }
  };

  const fs = mkFieldStyles(LEAD_THEME.green);
  const fsMultiline = { ...fs, '& .MuiInputBase-root': { height: 'auto', fontSize: 12, borderRadius: 1.5 }, '& .MuiInputBase-inputMultiline': { minHeight: 40 } };
  const fsAutocomplete = { ...fs, '& .MuiInputBase-root': { minHeight: 40, height: 'auto' } };

  // Find source option by value
  const getSourceOptionByValue = (value) => {
    return sourceOptions.find(option => option.value === value) || null;
  };

  // Find channel partner option by value
  const getChannelPartnerOptionByValue = (value) => {
    return channelPartnerOptions.find(option => option.value === value) || null;
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={handleClose}
      slotProps={{ backdrop: { invisible: false } }}
      PaperProps={{
        sx: {
          width: { xs: '100%', sm: DRAWER_WIDTH },
          maxWidth: '100%',
          boxShadow: '-6px 0 32px rgba(0,0,0,0.14)',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      {/* Header */}
      <Box sx={{ px: 2.5, pt: 2, pb: 1.75, bgcolor: LEAD_THEME.greenSf, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
          <Box>
            <Typography sx={{ fontSize: 18, fontWeight: 800, color: LEAD_THEME.green, lineHeight: 1.2 }}>Create New Lead</Typography>
            <Typography sx={{ fontSize: 11.5, color: LEAD_THEME.subtle, mt: 0.3 }}>Add contact and assignment details</Typography>
          </Box>
          <IconButton onClick={handleClose} size="small" sx={{ bgcolor: 'action.hover', '&:hover': { bgcolor: 'action.selected' }, mt: 0.25 }}>
            <CloseIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Box>
      </Box>

      {/* Body */}
      <Box sx={{ flex: 1, overflow: 'auto', p: 2, display: 'flex', flexDirection: 'column', gap: 1.75 }}>
        {errors.general && (
          <Alert severity="error" sx={{ fontSize: 12 }} onClose={() => setErrors(prev => ({ ...prev, general: '' }))}>
            {errors.general}
          </Alert>
        )}

        {/* Contact */}
        <Box>
          <SectionHeader title="Contact" subtitle="Name, email or mobile required" complete={!!(formData.name && (formData.email || formData.mobile))} />
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
            <TextField fullWidth required name="name" label="Name" value={formData.name} onChange={handleChange}
              error={!!errors.name} helperText={errors.name} sx={fs} size="small" />
            <TextField fullWidth name="email" label="Email" type="email" value={formData.email} onChange={handleChange}
              error={!!errors.email} helperText={errors.email} sx={fs} size="small" />
            {!errors.email && (
              <Typography sx={{ fontSize: 10.5, color: LEAD_THEME.subtle, ml: 1.5 }}>Either email or mobile is required</Typography>
            )}
            <Box sx={{ display: 'grid', gridTemplateColumns: '100px 1fr', gap: 1.25 }}>
              <FormControl fullWidth size="small" error={!!errors.country_code} sx={fs}>
                <InputLabel>Code</InputLabel>
                <Select name="country_code" value={formData.country_code} onChange={handleChange} label="Code">
                  {COUNTRY_CODES.map(option => (
                    <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField fullWidth name="mobile" label="Mobile" value={formData.mobile} onChange={handleChange}
                error={!!errors.mobile} helperText={errors.mobile} sx={fs} size="small" />
            </Box>
            {!errors.mobile && (
              <Typography sx={{ fontSize: 10.5, color: LEAD_THEME.subtle, ml: 1.5 }}>Either email or mobile is required</Typography>
            )}
          </Box>
        </Box>

        <Divider />

        {/* Source */}
        <Box>
          <SectionHeader title="Source" subtitle="Select existing or type new" complete={!!(formData.source && formData.source.trim())} />
          <Autocomplete
            options={sourceOptions}
            getOptionLabel={(option) => {
              if (typeof option === 'string') {
                const matchingOption = sourceOptions.find(opt => opt.value === option);
                if (matchingOption) return matchingOption.label;
                return option === 'manual' ? 'Manual' : option;
              }
              return option.value === 'manual' ? 'Manual' : option.label;
            }}
            isOptionEqualToValue={(option, value) => {
              if (typeof value === 'string') return option.value === value || option.label === value;
              return option.value === value?.value;
            }}
            value={formData.source || null}
            onChange={(event, newValue) => {
              const sourceValue = typeof newValue === 'string' ? newValue : newValue ? newValue.value : '';
              handleAutocompleteChange('source', sourceValue);
            }}
            onInputChange={(event, newInputValue) => { if (event) handleAutocompleteChange('source', newInputValue || ''); }}
            freeSolo
            renderInput={(params) => (
              <TextField {...params} label="Source" size="small" placeholder="Select or type" error={!!errors.source} helperText={errors.source} required sx={fsAutocomplete} />
            )}
            renderOption={(props, option) => (
              <li {...props}>
                <Typography variant="body2">{typeof option === 'string' ? option : option.value === 'manual' ? 'Manual' : option.label}</Typography>
              </li>
            )}
            ListboxProps={{ style: { fontSize: 12 } }}
            sx={{ '& .MuiAutocomplete-input': { fontSize: 12 } }}
          />
        </Box>

        <Divider />

        {/* Assignment */}
        <Box>
          <SectionHeader title="Assignment" subtitle="Assigned to and channel partner (optional)" complete={!!(formData.assigned_to || formData.channel_partner)} />
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
            <AgentSelect agents={agentsList} value={formData.assigned_to} onChange={handleAgentChange}
              label="Assigned To" name="assigned_to" variant="assign" multiple={false} showAvatar clearable size="small" themeVariant="lead"
              error={errors.assigned_to} helperText={errors.assigned_to} sx={fsAutocomplete} />
            <Autocomplete
              options={channelPartnerOptions}
              getOptionLabel={(option) => option.label || ''}
              value={getChannelPartnerOptionByValue(formData.channel_partner) || null}
              onChange={(event, newValue) => handleAutocompleteChange('channel_partner', newValue ? newValue.value : '')}
              renderInput={(params) => (
                <TextField {...params} label="Channel Partner (optional)" size="small" placeholder="Select" sx={fsAutocomplete} />
              )}
              ListboxProps={{ style: { fontSize: 12 } }}
              sx={{ '& .MuiAutocomplete-input': { fontSize: 12 } }}
            />
          </Box>
        </Box>

        <Divider />

        {/* Tags */}
        <Box>
          <SectionHeader title="Tags" subtitle="Optional labels for filtering" complete={formData.tags?.length > 0} />
          <Autocomplete
            multiple
            freeSolo
            options={tagOptions}
            value={formData.tags}
            onChange={handleTagsChange}
            getOptionLabel={(option) => (typeof option === 'string' ? option : option.label || option.value || '')}
            renderInput={(params) => (
              <TextField {...params} label="Tags (optional)" size="small" placeholder="Type to add" sx={fsAutocomplete} />
            )}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip {...getTagProps({ index })} key={option?.label ?? option ?? index}
                  label={typeof option === 'string' ? option : option.label || option.value || ''} size="small"
                  sx={{ height: 22, fontSize: '0.68rem', fontWeight: 600, bgcolor: LEAD_THEME.greenLt, color: LEAD_THEME.green, border: `1px solid ${LEAD_THEME.border}`, '& .MuiChip-deleteIcon': { fontSize: 13 } }} />
              ))
            }
            ListboxProps={{ style: { fontSize: 12 } }}
          />
        </Box>

        <Divider />

        {/* Notes */}
        <Box>
          <SectionHeader title="Notes" subtitle="Optional details" complete={!!(formData.notes && formData.notes.trim())} />
          <TextField fullWidth name="notes" label="Notes (optional)" multiline minRows={2} value={formData.notes} onChange={handleChange}
            placeholder="Add any additional notes" size="small" sx={fsMultiline} />
        </Box>
      </Box>

      {/* Footer */}
      <Box sx={{ px: 2, py: 1.5, borderTop: '1px solid', borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: LEAD_THEME.bg }}>
        <Button
          variant="outlined"
          onClick={handleClose}
          sx={{ color: LEAD_THEME.green, borderColor: `${LEAD_THEME.green}55`, textTransform: 'none', fontWeight: 600, fontSize: 12, height: 36, bgcolor: LEAD_THEME.greenSf, '&:hover': { borderColor: LEAD_THEME.green, bgcolor: LEAD_THEME.greenLt } }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={isLoading}
          startIcon={isLoading ? <CircularProgress size={16} color="inherit" /> : null}
          sx={{ bgcolor: LEAD_THEME.green, textTransform: 'none', fontWeight: 700, fontSize: 12, height: 36, px: 2.5, boxShadow: `0 2px 8px ${LEAD_THEME.greenLt}`, '&:hover': { bgcolor: LEAD_THEME.greenMd } }}
        >
          {isLoading ? 'Creating...' : 'Create Lead'}
        </Button>
      </Box>
    </Drawer>
  );
};

export default CreateNewLead;
